import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Radio,
  RadioGroup,
  FormControl,
  Alert,
  CircularProgress,
} from "@mui/material";
import useIsMobile from "../../hooks/useIsMobile";
import {
  updateSubscriptionPlanById,
  getAvailablePlans,
  AvailablePlan,
} from "../../api/SubscriptionAPI";
import { getCompanyData } from "../../utils/generalUtil";

interface PlanSelectionStepProps {
  onComplete: () => void;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
  isEnabled: boolean;
}

const PlanSelectionStep: React.FC<PlanSelectionStepProps> = ({
  onComplete,
  onError,
  onSuccess,
  isEnabled,
}) => {
  const isMobile = useIsMobile();
  const [selectedPlan, setSelectedPlan] = useState<number>(2);
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState<AvailablePlan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(true);

  // Carregar planos do backend
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const availablePlans = await getAvailablePlans();
        setPlans(availablePlans);
      } catch (error) {
        console.error("Erro ao carregar planos:", error);
        onError("Erro ao carregar planos disponíveis");
      } finally {
        setLoadingPlans(false);
      }
    };

    if (isEnabled) {
      fetchPlans();
    }
  }, [isEnabled, onError]);

  const handlePlanSelect = (planId: number) => {
    setSelectedPlan(planId);
  };

  const handleContinue = async () => {
    if (!isEnabled) return;

    setLoading(true);
    try {
      const companyData = getCompanyData();
      if (!companyData?.companyId) {
        throw new Error("Dados da empresa não encontrados");
      }

      const companyId = Number(companyData.companyId);

      const success = await updateSubscriptionPlanById(companyId, selectedPlan);

      if (success) {
        onSuccess("Plano selecionado com sucesso!");
        onComplete();
      } else {
        throw new Error("Erro ao atualizar plano");
      }
    } catch (error: any) {
      onError(error.message || "Erro ao selecionar plano");
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price: number) => {
    return `R$ ${price.toFixed(2).replace(".", ",")}`;
  };

  if (!isEnabled) {
    return (
      <Alert severity="info" sx={{ marginBottom: "16px" }}>
        Complete os passos anteriores para selecionar um plano.
      </Alert>
    );
  }

  if (loadingPlans) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "40px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (plans.length === 0) {
    return (
      <Alert severity="error">
        Não foi possível carregar os planos disponíveis.
      </Alert>
    );
  }

  return (
    <Box>
      <Typography
        variant="body1"
        sx={{
          color: "#666",
          marginBottom: isMobile ? "16px" : "24px",
          fontSize: isMobile ? "14px" : "16px",
          textAlign: isMobile ? "center" : "left",
        }}
      >
        Escolha o plano ideal para o seu negócio. Você pode alterar a qualquer
        momento.
      </Typography>

      <FormControl component="fieldset" fullWidth>
        <RadioGroup
          value={selectedPlan}
          onChange={(e) => handlePlanSelect(Number(e.target.value))}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              flexWrap: isMobile ? "nowrap" : "wrap",
              gap: isMobile ? "12px" : "16px",
              justifyContent: "center",
            }}
          >
            {plans.map((plan) => (
              <Card
                key={plan.id}
                sx={{
                  flex: isMobile ? "1" : "0 1 calc(33.333% - 11px)",
                  minWidth: isMobile ? "auto" : "280px",
                  maxWidth: isMobile ? "auto" : "320px",
                  cursor: "pointer",
                  border:
                    selectedPlan === plan.id
                      ? "2px solid #226FE2"
                      : "1px solid #e0e0e0",
                  borderRadius: isMobile ? "8px" : "12px",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: "#226FE2",
                    boxShadow: "0 4px 12px rgba(34, 111, 226, 0.15)",
                  },
                }}
                onClick={() => handlePlanSelect(plan.id)}
              >
                <CardContent
                  sx={{
                    padding: isMobile ? "16px" : "20px",
                    "&:last-child": {
                      paddingBottom: isMobile ? "16px" : "20px",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      marginBottom: "16px",
                    }}
                  >
                    <Radio
                      value={plan.id}
                      sx={{
                        color: "#226FE2",
                        "&.Mui-checked": {
                          color: "#226FE2",
                        },
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          marginBottom: "8px",
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: "#22303E",
                            fontSize: isMobile ? "16px" : "16px",
                          }}
                        >
                          {plan.name}
                        </Typography>
                        {plan.isRecommended && (
                          <Chip
                            label="Recomendado"
                            size="small"
                            sx={{
                              backgroundColor: "#226FE2",
                              color: "white",
                              fontSize: isMobile ? "10px" : "12px",
                              height: isMobile ? "20px" : "24px",
                            }}
                          />
                        )}
                      </Box>

                      <Typography
                        variant="body2"
                        sx={{
                          color: "#666",
                          marginBottom: isMobile ? "12px" : "16px",
                          fontSize: isMobile ? "13px" : "14px",
                        }}
                      >
                        {plan.description}
                      </Typography>

                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 700,
                          color: "#226FE2",
                          marginBottom: isMobile ? "8px" : "12px",
                          fontSize: isMobile ? "18px" : "20px",
                        }}
                      >
                        {formatPrice(plan.price)}/mês
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: isMobile ? 0.5 : 0.5,
                        }}
                      >
                        {plan.benefits.map((benefit, index) => (
                          <Typography
                            key={index}
                            variant="body2"
                            sx={{
                              color: "#333",
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              fontSize: isMobile ? "12px" : "14px",
                            }}
                          >
                            <Box
                              sx={{
                                width: isMobile ? "4px" : "6px",
                                height: isMobile ? "4px" : "6px",
                                borderRadius: "50%",
                                backgroundColor: "#226FE2",
                                flexShrink: 0,
                              }}
                            />
                            {benefit}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </RadioGroup>
      </FormControl>

      <Box sx={{ marginTop: isMobile ? "24px" : "32px", textAlign: "center" }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleContinue}
          disabled={loading}
          fullWidth={isMobile}
          sx={{
            backgroundColor: "#226FE2",
            color: "white",
            fontWeight: 600,
            padding: isMobile ? "14px 24px" : "12px 32px",
            borderRadius: "8px",
            fontSize: isMobile ? "16px" : "14px",
            "&:hover": {
              backgroundColor: "#1a5bb8",
            },
            "&:disabled": {
              backgroundColor: "#ccc",
            },
          }}
        >
          {loading ? (
            <CircularProgress size={24} sx={{ color: "white" }} />
          ) : (
            "Continuar"
          )}
        </Button>
      </Box>

      <Typography
        variant="caption"
        sx={{
          display: "block",
          textAlign: "center",
          marginTop: isMobile ? "12px" : "16px",
          color: "#666",
          fontSize: isMobile ? "12px" : "14px",
        }}
      >
        Você pode alterar seu plano a qualquer momento nas configurações da
        empresa.
      </Typography>
    </Box>
  );
};

export default PlanSelectionStep;
